import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import { Calendar2Outline, ClockOutline } from '@lyearn/icons';
import { Avatar } from '@lyearn/ui';
import { PARENT_ENTITY_ID } from '@/modules/corporate/modules/exercise/views/Details/utils';
import {
  CurrentReviewingState,
  ExerciseReviewType,
  ExerciseType,
  PerformanceErrorCode,
  PerformanceInteractionErrorCodeEnum,
  PerformanceStatusEnum,
  ResourceReviewTypeEnum,
  ReviewerTypeEnum,
  SubmissionData,
} from '@/types/schema';
import getHumanDuration from '@/utils/time/getHumanDuration';

import getLatestExerciseSubmission from '../../../helpers/getLatestExerciseSubmission';
import { ExercisePageMode } from '../../../types';
import {
  ExerciseReviewPerformanceFragment,
  ExerciseReviewPerformanceSubmissionsFragment,
} from '../graphql/fragments/generated/ExerciseReviewPerformance';
import { ExerciseDialogs } from '../hooks/useExerciseActions';

/**
 * Get date in the format of 'd MMM, h:mm aa'
 * @param date
 * @returns {string} formatted date
 */
const getFormattedDate = (date?: string | null) => {
  if (date) {
    return formatDate(date, DateFormat.DateTime);
  }

  return '';
};

/**
 * Get the details list for details tab on submission page in details tab
 * @param t
 * @param exercise
 * @param performance
 * @param currentSubmission
 * @param isPeerReview
 * @returns Array of details list components
 */
export const getExerciseDetailsList = (
  t: TFunction,
  exercise: ExerciseType,
  performance: ExerciseReviewPerformanceFragment,
  currentSubmission?: ExerciseReviewPerformanceSubmissionsFragment,
  isPeerReview?: boolean,
) => {
  const reviewedBy = currentSubmission?.reviewedBy || performance.reviewers[0];

  return getEnabledItems([
    {
      id: 'DURATION',
      label: t('Common:Duration'),
      renderer: {
        Icon: <ClockOutline className="h-20 w-20" fillColorToken="icon-primary" />,
        value: getHumanDuration(exercise.duration),
      },
      enabled: true,
    },
    {
      id: 'SUBMISSION_BY',
      label: t('Exercise:SubmissionBy'),
      renderer: {
        Icon: <Avatar size="x-small" src={performance.user?.profilePicture || undefined} />,
        value: performance.user?.name || '',
      },
      enabled: !isPeerReview,
    },
    {
      id: 'SUBMITTED_ON',
      label: t('Exercise:SubmittedOn'),
      renderer: {
        Icon: <Calendar2Outline className="h-20 w-20" fillColorToken="icon-primary" />,
        value: getFormattedDate(currentSubmission?.submittedAt),
      },
      enabled: !isPeerReview,
    },
    {
      id: 'REVIEW_BY',
      label: t('Exercise:ReviewBy'),
      renderer: {
        Icon: <Avatar size="x-small" src={reviewedBy?.profilePicture || undefined} />,
        value: reviewedBy?.name || reviewedBy?.emailId || '',
      },
      enabled: !!reviewedBy && !isPeerReview,
    },
    {
      id: 'REVIEWED_ON',
      label: t('Exercise:ReviewedOn'),
      renderer: {
        Icon: <Calendar2Outline className="h-20 w-20" fillColorToken="icon-primary" />,
        value: getFormattedDate(currentSubmission?.reviewedAt),
      },
      enabled: !!currentSubmission?.reviewedAt && !isPeerReview,
    },
  ]);
};

/**
 * The functions take the performance and checks if the performance is not Completed or Rejected and the submission is latest.
 * If the results return truthy we assume that the reviewer is reviewing, otherwise reviewer might be viewing past submission.
 *
 * @param {boolean} isReviewer - Whether the user is a reviewer or not
 * @param {ExercisePerformanceType} performance - The exercise performance, used to get submissions
 * @param {string} inViewSubmissionId - Used to find the latest submission from submissions
 * @returns
 */
export const isReviewerInReviewMode = ({
  isReviewer,
  inViewSubmissionId,
  performance,
  exercise,
}: {
  isReviewer: boolean;
  inViewSubmissionId: string;
  performance?: ExerciseReviewPerformanceFragment | null;
  exercise: ExerciseType;
}) => {
  if (!isReviewer) {
    return false;
  }

  if (performance) {
    const { submissions, currentReviewingState } = performance;

    const { isLatestSubmission } = getLatestExerciseSubmission(submissions, inViewSubmissionId);

    return (
      !isReviewComplete(performance, exercise) &&
      isLatestSubmission &&
      (currentReviewingState === null || currentReviewingState?.isCurrentUserReviewing)
    );
  }

  return true;
};

/**
 * This is used to show dialog when there are to concurrent reviewers, reviewing the same dialog
 *
 * @param currentReviewingState - What is the reviewing state of the submission
 * @param setConcurrentDialogState - State fnc which enables the particular dialog
 * @returns - undefined
 */
export const showConcurrentReviewingDialog = (
  currentReviewingState?: CurrentReviewingState | null,
  setConcurrentDialogState?: (payload: {
    dialogType: ExerciseDialogs | null;
    name: string;
  }) => void,
) => {
  if (!currentReviewingState || !setConcurrentDialogState) return null;

  setConcurrentDialogState({
    dialogType: resolveReviewedDialogType(currentReviewingState?.status),
    name: currentReviewingState?.reviewBy.name || '',
  });
};

/**
 * It checks for `AlreadyReviewed` string in two different enums. `PerformanceErrorCode` and `PerformanceInteractionErrorCodeEnum`
 * @param errorCode - The error code
 * @returns boolean
 */
export const isSubmissionAlreadyReviewed = (errorCode: string | null | undefined) => {
  return (
    errorCode === PerformanceErrorCode.AlreadyReviewed ||
    errorCode === PerformanceInteractionErrorCodeEnum.AlreadyReviewed
  );
};

/**
 * Based on the status of reviewing state, it returns the dialog type.
 * @param {string} status - PerformanceStatusEnum
 * @param useSubmissionAlreadyReviewed - Use SubmissionAlreadyReviewed instead of AlreadyReviewed in case of Completed status.
 * @returns The dialog type
 */

export const resolveReviewedDialogType = (
  status?: PerformanceStatusEnum,
  useSubmissionAlreadyReviewed?: boolean,
) => {
  if (status === PerformanceStatusEnum.Completed) {
    return useSubmissionAlreadyReviewed
      ? ExerciseDialogs.SubmissionAlreadyReviewed
      : ExerciseDialogs.AlreadyReviewed;
  }

  if (status === PerformanceStatusEnum.InProgress) return ExerciseDialogs.CurrentlyReviewing;

  return null;
};

export const getSubmissionInNumber = (
  inViewSubmissionId: string,
  submissionsToReview?: SubmissionData[],
) => {
  const submissionNumber =
    submissionsToReview?.findIndex((s) => s.submissionId === inViewSubmissionId) ?? -1;

  return submissionNumber === -1 ? undefined : submissionNumber + 1;
};

export const getSubmitterName = (inViewSubmissionId: string, submissions?: SubmissionData[]) => {
  const submission = submissions?.find(
    (submission) => submission.submissionId === inViewSubmissionId,
  );

  return { name: submission?.user?.name, profilePicture: submission?.user?.profilePicture };
};

export const getPeerName = (
  reviewResults?: ExerciseReviewType[] | null,
  reviewerIdForPeerReview?: string,
) => {
  return reviewResults?.find((r) => {
    return r.reviewerId === reviewerIdForPeerReview;
  })?.name;
};

type CanReviewType = {
  exercise: ExerciseType;
  submissionsToReview?: SubmissionData[];
  inViewSubmissionId?: string;
  mode?: ExercisePageMode;
};

export const resolveCanReview = ({
  exercise,
  submissionsToReview,
  inViewSubmissionId,
  mode,
}: CanReviewType) => {
  return exercise.reviewerConfig?.type === ReviewerTypeEnum.Peer
    ? !!submissionsToReview?.find((s) => s.submissionId === inViewSubmissionId)
    : mode === ExercisePageMode.Reviewer && exercise.isReviewer;
};

export const isSubmissionReviewed = (status: PerformanceStatusEnum) => {
  return [PerformanceStatusEnum.Completed, PerformanceStatusEnum.Rejected].includes(status);
};

export const isReviewComplete = (
  performance: ExerciseReviewPerformanceFragment,
  exercise: ExerciseType,
) => {
  const optionalReviewSubmitted =
    exercise.reviewType !== ResourceReviewTypeEnum.OptionalReview ||
    Boolean(performance.reviewResults?.length);

  return optionalReviewSubmitted && isSubmissionReviewed(performance.status);
};

export const appendParentEntityId = (parentEntityId?: string) =>
  `${parentEntityId ? `?${PARENT_ENTITY_ID}=${parentEntityId}` : ''}`;
